import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Zap, Share2, Edit3, Trash2, Info, MapPin, Calendar, GraduationCap } from 'lucide-react';
import './PositionCard.css';
import ReportModal from './ReportModal';

export default function PositionCard({ position, onView, onToggleSave, onDelete, onMatch, onEdit }) {
  const navigate = useNavigate();

  // State
  const [isSaved, setIsSaved] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [isReported, setIsReported] = useState(false);

  // User data
  const userData = JSON.parse(localStorage.getItem('user')) || {};
  const userId = userData.id || userData._id;
  const userRole = userData.role;

  // Professor check
  const isProfessorsPosition = userRole === 'professor' && position.professor === userId;

  // Load saved state (students only)
  useEffect(() => {
    if (!userId || !position._id || isProfessorsPosition) return;

    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/api/saved/${userId}`)
      .then(response => {
        const savedIds = response.data.savedItems.map(item => item._id);
        setIsSaved(savedIds.includes(position._id));
      })
      .catch(error => {
        console.error('Error loading saved state:', error);
      });
  }, [position._id, userId, isProfessorsPosition]);

  // Prevent body scroll when delete modal open
  useEffect(() => {
    document.body.style.overflow = showDeleteConfirmation ? 'hidden' : 'unset';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showDeleteConfirmation]);

  // Save toggle (students only)
  const handleSaveToggle = (event) => {
    event.stopPropagation();
    if (!userId || !position._id) return;

    const url = `${process.env.REACT_APP_BACKEND_URL}/api/saved/${userId}/${position._id}`;
    const request = isSaved ? axios.delete(url) : axios.post(url);

    request
      .then(() => {
        const newSavedState = !isSaved;
        setIsSaved(newSavedState);
        if (!newSavedState && onToggleSave) {
          onToggleSave(position._id);
        }
      })
      .catch(error => {
        console.error('Error toggling save state:', error);
      });
  };

  // Delete handlers
  const handleDeleteClick = (event) => {
    event.stopPropagation();
    setShowDeleteConfirmation(true);
  };

  const handleConfirmDelete = async (event) => {
    event.stopPropagation();
    setIsDeleting(true);
    try {
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/positions/${position._id}`,
        { headers: { Authorization: `Bearer ${userData.token}` } }
      );
      setShowDeleteConfirmation(false);
      if (onDelete) onDelete(position._id);
      alert('✅ Position deleted successfully');
    } catch (error) {
      console.error('Error deleting position:', error);
      alert('❌ Failed to delete position');
      setShowDeleteConfirmation(false);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelDelete = (event) => {
    event.stopPropagation();
    setShowDeleteConfirmation(false);
  };

  const handleReportClick = (event) => {
    event.stopPropagation();
    setShowReportModal(true);
  };

  const handleReportSubmit = async (reportReason) => {
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/positions/${position._id}/report`,
        {
          reason: reportReason,
          positionTitle: position.title,
          positionInstitution: position.institution,
          reporterName: userData.name,
          reporterEmail: userData.email
        },
        { headers: { Authorization: `Bearer ${userData.token}` } }
      );
      setIsReported(true);
      alert('✅ Report submitted successfully!');
    } catch (error) {
      alert('❌ Failed to submit report.');
    }
  };

  // Format helpers
  const getFieldPills = () => {
    if (!position.fields?.length) return [<span key="none" className="field-pill none">No fields</span>];

    let fieldsArray = Array.isArray(position.fields) ? position.fields : position.fields.split(',').map(f => f.trim());
    const limit = 2;
    const items = fieldsArray.slice(0, limit).map((f, i) => (
      <span key={i} className="field-pill">{f}</span>
    ));

    if (fieldsArray.length > limit) {
      items.push(<span key="more" className="field-pill more">+{fieldsArray.length - limit} more</span>);
    }
    return items;
  };

  const formatLocation = () => {
    if (position.city && position.country) return `${position.city}, ${position.country}`;
    return position.city || position.country || 'Remote / Unspecified';
  };

  const formatStartSemester = () => {
    if (!position.start_semester && !position.start_year) return 'Date TBD';
    if (position.start_semester && position.start_year) return `${position.start_semester} ${position.start_year}`;
    return position.start_semester || position.start_year || 'Date TBD';
  };

  const location = formatLocation();
  const startDate = formatStartSemester();

  const handleMatchClick = (event) => {
    event.stopPropagation();
    if (onMatch) onMatch(position);
  };

  const handleLinkedInShare = (event) => {
    event.stopPropagation();
    const url = window.location.origin + `/position/${position._id}`;
    const postText = `📢 New PhD Position: ${position.title} at ${position.institution}! Check it out on NeuHawk.`;
    const linkedInUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}&text=${encodeURIComponent(postText)}`;
    window.open(linkedInUrl, '_blank', 'width=600,height=400');
  };

  const DeleteConfirmationModal = () => (
    <div className="delete-confirmation-overlay" onClick={handleCancelDelete}>
      <div className="delete-confirmation-modal" onClick={(e) => e.stopPropagation()}>
        <div className="confirmation-header">
          <h3 className="confirmation-title">Confirm Deletion</h3>
        </div>
        <div className="confirmation-body">
          <p className="confirmation-message">Are you sure you want to delete <strong>{position.title}</strong>?</p>
          <p className="confirmation-warning">This action cannot be undone.</p>
        </div>
        <div className="confirmation-actions">
          <button onClick={handleCancelDelete} className="cancel-button" disabled={isDeleting}>Cancel</button>
          <button onClick={handleConfirmDelete} className="confirm-delete-button" disabled={isDeleting}>
            {isDeleting ? 'Deleting...' : 'Delete Position'}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <div
        className="position-card"
        onClick={(e) => {
          if (isProfessorsPosition) {
            if (onEdit) onEdit(position);
          } else {
            onView();
          }
        }}
      >
        <div className="card-ui-overlay">
          <div className="badge-group">
            {userRole !== 'professor' && position.financial_info?.fully_funded === 'Fully Funded' && (
              <div className="status-badge funded">fully-funded</div>
            )}
            {userRole !== 'professor' && position.financial_info?.fully_funded === 'Partially Funded' && (
              <div className="status-badge partially-funded">partially-funded</div>
            )}
            {position.native && !isProfessorsPosition && <div className="native-badge">Native</div>}
            {position.status === 'draft' && <div className="status-badge draft">Draft</div>}
            {position.status === 'archived' && <div className="status-badge archived">Archived</div>}
            {(position.status === 'active' || !position.status) && isProfessorsPosition && (
              <div className="status-badge live">Live</div>
            )}
          </div>

          <div className="action-group">
            {isProfessorsPosition ? (
              <>
                <button className="match-btn-premium" onClick={handleMatchClick}>
                  <Zap size={14} />
                  Match
                </button>
                <button className="icon-btn share" onClick={handleLinkedInShare} title="Share to LinkedIn">
                  <Share2 size={16} />
                </button>
                <button
                  className="icon-btn edit"
                  onClick={(e) => { e.stopPropagation(); if (onEdit) onEdit(position); }}
                  title="Edit Position"
                >
                  <Edit3 size={16} />
                </button>
                <button className="icon-btn delete" onClick={handleDeleteClick} disabled={isDeleting} title="Delete Position">
                  <Trash2 size={16} />
                </button>
              </>
            ) : (
              <>
                <button className="icon-btn save" onClick={handleSaveToggle} title={isSaved ? 'Unsave' : 'Save'}>
                  <svg className={`heart-icon ${isSaved ? 'saved' : 'unsaved'}`} viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                </button>
                <button className="icon-btn report" onClick={handleReportClick} disabled={isReported} title="Report Issue">
                  <Info size={16} />
                </button>
              </>
            )}
          </div>
        </div>

        <div className="card-header">
          <h3 className="position-title">{position.title}</h3>
          <p className="institution-name">{position.institution || 'University Not Specified'}</p>
        </div>

        <div className="card-content">
          <div className="metadata-grid">
            <div className="meta-item">
              <MapPin size={14} className="meta-icon" />
              <span className="meta-text">{location}</span>
            </div>
            <div className="meta-item">
              <Calendar size={14} className="meta-icon" />
              <span className="meta-text">{startDate}</span>
            </div>
          </div>

          <div className="research-fields-section">
            <div className="fields-header">
              <GraduationCap size={14} className="meta-icon" />
              <span className="fields-label">Research Fields</span>
            </div>
            <div className="field-pills-container">
              {getFieldPills()}
            </div>
          </div>
        </div>

        <div className="card-footer">
          {isProfessorsPosition ? (
            <div className="professor-footer-content">
              <button
                className="applications-cta"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/professor/applications/${position._id}`);
                }}
              >
                Manage Applications
              </button>
              <div className="stats-indicator">
                <span className="count">{position.applicationCount || 0}</span>
                <span className="label">Applicants</span>
              </div>
            </div>
          ) : (
            <button className="view-button">View Full Details</button>
          )}
        </div>
      </div>

      {showDeleteConfirmation && ReactDOM.createPortal(<DeleteConfirmationModal />, document.body)}

      <ReportModal
        isOpen={showReportModal}
        onClose={() => setShowReportModal(false)}
        position={position}
        onSubmit={handleReportSubmit}
      />
    </>
  );
}
